import {
  Button,
  Container,
  Form,
  Header,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { createRef, useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../app/hook";

import { usePostSwitchPaymentProviderMutation } from "../../features/api";
import { Messages, getMessage } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { getExt } from "../../utils/common";
import { formatLabel } from "../../utils/format";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import ErrorTable, {
  MessageItem,
  getMessageItemList,
} from "../UI/table/ErrorTable";
import { COMMON_LABELS, COMMON_CONFIGS } from "../default-constants";
import { CONFIGS, CONTENTS, LABELS } from "./constants";

export interface FileItem {
  label: string;
  dismissLabel: string;
}

/**
 * Omni PG 切替画面
 */
export const Main = () => {
  const dispatch = useAppDispatch();

  // inputRef
  const inputRef = useRef<HTMLInputElement>(null);
  // アップロードファイル
  const [uploadFile, setUploadFile] = useState<File>();
  // エラーテキスト
  const [errorText, setErrorText] = useState("");

  // 決済事業者切替API設定
  const [uploadSwitchPaymentProviderFiles] =
    usePostSwitchPaymentProviderMutation();

  // スピナー表示処理用
  const [isSpinnerDisplay, setIsSpinnerDisplay] = useState(false);
  // ボタン非活性用
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // エラーメッセージ表示処理用
  const [errorMessageList, setErrorMessageList] = useState<MessageItem[]>([]);

  // 提出用ファイル設定
  const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files?.[0]) {
      return;
    }

    // アップロードされたファイルの拡張子が「.xlsx」以外の場合はアラートを出して終了する。
    if (CONFIGS.FILE_EXTENSION !== getExt(files[0].name.toLowerCase())) {
      const messageId = "KGRERR057" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
      return;
    }

    // 送信するファイル情報を設定
    setUploadFile(files[0]);

    // エラーテキストを設定
    setErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  // アップロードボタンイベント
  const uploadOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    inputRef.current?.click();
  };

  // 申請ボタン押下処理
  const onClickButton = async () => {
    const fileData = new FormData();

    if (!inputCheck()) {
      return;
    }
    // 確認ダイアログ表示メッセージ作成
    let contentMessage = COMMON_LABELS.CONTENTS.SWITCH_CONFIRM;

    if (uploadFile !== undefined) {
      // アップロードファイル設定処理
      fileData.append(CONFIGS.FILE, uploadFile);
      fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.APPLICATION_FILE);
      //　確認ダイアログ表示メッセージ追記
      contentMessage += formatLabel(CONTENTS.APPLICATION_FILE_NAME, [
        uploadFile.name,
      ]);
    }

    //　確認ダイアログ表示
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: contentMessage,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        positiveFn: () => {
          merchantApplication(fileData);
        },
      })
    );

    /** 同じファイルを連続アップロードできるように処理 */
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  // 入力チェック
  const inputCheck = () => {
    let errorFlag = true;

    if (uploadFile === undefined) {
      // ファイル未選択時のエラー表示
      const messageId = "KGRERR054" as keyof Messages;
      // エラーテキストを設定
      setErrorText(getMessage(messageId).label);
      errorFlag = false;
    }

    return errorFlag;
  };

  // 確認ボタンOK押下時の処理
  const merchantApplication = async (fileData: FormData) => {
    // スピナー起動
    setIsSpinnerDisplay(true);
    // ボタン非活性
    setIsButtonDisabled(true);

    // エラーテキストを初期化
    setErrorText("");

    // 決済事業者切替API呼び出し
    uploadSwitchPaymentProviderFiles(fileData)
      .unwrap()
      .then((res) => {
        // エラー内容を初期化
        setErrorMessageList([]);

        // アップロード後初期化
        setUploadFile(undefined);

        if (COMMON_CONFIGS.API.STATUS_ERROR === res.status) {
          if (
            res.detailMessageList !== null &&
            res.detailMessageList.length !== 0
          ) {
            setErrorMessageList(getMessageItemList(res.detailMessageList));
          }
        }
      })
      .finally(() => {
        // スピナー停止
        setIsSpinnerDisplay(false);
        // ボタン活性
        setIsButtonDisabled(false);
      });
  };

  // ref を作成しスクロールさせたい場所にある Element にセット
  const errorRef = createRef<HTMLDivElement>();
  // このコールバックを呼び出して ref.current.scrollIntoView() を呼び出してスクロール
  const scrollToBottomOfList = useCallback(() => {
    // エラー内容にスクロール
    errorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [errorRef]);

  useEffect(() => {
    // ページが描画されたらリストの末尾までスクロール
    scrollToBottomOfList();
  }, [scrollToBottomOfList, errorMessageList]);

  return (
    <Container
      header={
        <Header variant="h2" description="">
          {LABELS.APPLICATION_FILE}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="xxl">
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="l">
              {isSpinnerDisplay ? <Spinner size="big" /> : <></>}
              <Button
                iconAlign="right"
                onClick={onClickButton}
                variant="primary"
                disabled={isButtonDisabled}
              >
                {COMMON_LABELS.BTNS.SWITCH}
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="vertical" size="xxl">
            <FileUpload
              inputRef={inputRef}
              label={""}
              name={"file"}
              placeholder={LABELS.FILE_PLACEHOLDER}
              accept={CONFIGS.FILE_EXTENSION}
              fileName={uploadFile ? uploadFile.name : ""}
              setUploadFile={setUploadFile}
              errorText={errorText}
              setErrorText={setErrorText}
              onChange={onChangeFile}
              onUpload={uploadOnClick}
            />
          </SpaceBetween>
        </Form>
        <SpaceBetween direction="vertical" size="xxs">
          {errorMessageList.length !== 0 ? (
            <>
              <ErrorTable data={errorMessageList} title={LABELS.ERROR_TITLE} />
              <div id="alternativeDirectError" ref={errorRef} />
            </>
          ) : (
            <></>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};
